import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Container,
    Typography,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Button,
    Divider,
    Grid,
    Card,
    CardContent,
    useTheme
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Book as BookIcon,
    ArrowForward as ArrowForwardIcon,
    Help as HelpIcon
} from '@mui/icons-material';
import Seo from '../components/common/Seo';

function HelpCenter() {
    const theme = useTheme();

    const faqItems = [
        {
            question: 'ROOMSHAREとは？',
            answer: 'ROOMSHAREは、ルームシェアの物件情報や、相性の良いルームシェア相手を簡単に探せるプラットフォームです。'
        },
        {
            question: '利用料金は無料ですか？',
            answer: '基本的な物件検索や閲覧、メッセージのやり取りは無料でご利用いただけます。'
        },
        {
            question: '物件を掲載するには？',
            answer: 'アカウント登録後、「物件を掲載」ボタンから物件情報を登録できます。'
        },
        {
            question: 'ルームシェアの安全性について',
            answer: '当プラットフォームではユーザーの安全を最優先に考え運営を行っておりますが、利用に際してはご自身でも安全を確保し、自己責任で対応をお願いいたします。詳しくは「安全なルームシェアのために」ガイドをご参照ください。'
        },
        {
            question: 'サポートへの連絡方法',
            answer: 'サポートが必要な場合は、ヘルプセンターの「お問い合わせ」フォームからご連絡ください。通常72時間以内に対応しています。'
        },
        {
            question: 'アカウント削除方法',
            answer: 'アカウント削除は、プロフィール設定ページから行えます。詳しくは「プロフィール設定」ページをご覧ください。'
        },
        {
            question: '物件掲載の一時停止について',
            answer: '物件掲載を一時的に停止するには、「登録した物件」ページから停止手続きを行ってください。再開する際は同じページから手続きを進めることができます。'
        },
        // 他のFAQ項目を追加
    ];

    const guideLinks = [
        {
            title: 'ルームシェアの始め方',
            description: 'ルームシェアを始める前に知っておきたい基本情報や準備について解説します。',
            link: '/guide/getting-started'
        },
        {
            title: '募集の書き方のコツ',
            description: '魅力的な募集投稿を作成するためのポイントやテクニックを紹介します。',
            link: '/guide/how-to-post'
        },
        {
            title: '安全なルームシェアのために',
            description: 'ルームシェアを安全に楽しむための注意点や確認事項について解説します。',
            link: '/guide/safe-roomshare'
        }
    ];

    return (
        <>
            <Seo
                title="ヘルプセンター | ルームシェアマッチング"
                description="ルームシェアマッチングの使い方やよくある質問をまとめたヘルプセンターです。アカウント設定、物件の投稿、メッセージの送信など、様々な操作方法を解説しています。"
                path="/help"
                keywords="ルームシェア,ヘルプ,使い方,FAQ,操作方法,サポート"
            />
            <Container maxWidth="md">
                <Paper sx={{ p: { xs: 2, md: 4 }, mt: 4, mb: 4, borderRadius: 2 }}>
                    <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        ヘルプセンター
                    </Typography>

                    <Typography variant="body1" color="text.secondary" paragraph>
                        ルームシェアのご利用に関するよくある質問や、サービスの使い方などをご案内しています。
                        より詳しい情報は各種ガイドをご覧ください。
                    </Typography>

                    {/* ガイドへのリンク */}
                    <Box sx={{ my: 4 }}>
                        <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                            <BookIcon color="primary" sx={{ mr: 1 }} />
                            ガイド
                        </Typography>

                        <Grid container spacing={3}>
                            {guideLinks.map((guide, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Card
                                        sx={{
                                            height: '100%',
                                            transition: 'transform 0.2s',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: 3
                                            }
                                        }}
                                    >
                                        <CardContent>
                                            <Typography variant="h6" component="h3" gutterBottom>
                                                {guide.title}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                {guide.description}
                                            </Typography>
                                            <Button
                                                component={RouterLink}
                                                to={guide.link}
                                                color="primary"
                                                endIcon={<ArrowForwardIcon />}
                                                size="small"
                                            >
                                                詳しく見る
                                            </Button>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>

                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                component={RouterLink}
                                to="/guide"
                                color="primary"
                                endIcon={<ArrowForwardIcon />}
                            >
                                すべてのガイドを見る
                            </Button>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    {/* よくある質問 */}
                    <Box sx={{ mt: 4 }}>
                        <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                            <HelpIcon color="primary" sx={{ mr: 1 }} />
                            よくある質問
                        </Typography>

                        {faqItems.map((item, index) => (
                            <Accordion key={index} sx={{ mb: 1 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography sx={{ fontWeight: 'medium' }}>{item.question}</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography>{item.answer}</Typography>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Box>

                    {/* お問い合わせへの誘導 */}
                    <Box sx={{ mt: 4, p: 3, bgcolor: '#F5F5F5', borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            解決しない問題がありますか？
                        </Typography>
                        <Typography variant="body2" paragraph>
                            FAQやガイドを確認しても解決しない問題がある場合は、お気軽にお問い合わせください。
                        </Typography>
                        <Button
                            component={RouterLink}
                            to="/contact"
                            variant="contained"
                            color="primary"
                        >
                            お問い合わせフォームへ
                        </Button>
                    </Box>
                </Paper>
            </Container>
        </>
    );
}

export default HelpCenter; 